import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type DragEvent, type MouseEvent as ReactMouseEvent } from "react";
import { createPortal } from "react-dom";
import { BOARD_BACKGROUNDS, BOARD_SWATCHES, blankCard, columnChanged, enqueueBoardWrite, flushBoardWrites, freshId, isBoardName, isPipeBoard, itemTitle, parseBoard, plainSuivi, renderBoard, suiviDirty, todayStamp, watchActive, type BoardFile, type BoardProperty, type BoardWatch, type CardNotice } from "./atelier";
import { deliverNotice, loadNotifyPeople, NoticeLine, sendVeilCopy, SendVeil, type NotifyPerson } from "./boardNotify";
import { currentUserId } from "./auth";
import { coffreLocked, copyVaultFile, createCoffreFolder, createCoffreMarkdown, deleteCoffreItem, deletePageAndMedia, listCoffre, readCoffreText, saveCoffreText, type CoffreEntry } from "./coffre";
import { CoffreLockedLine, useCoffreUnlocked } from "./CoffreUnlock";
import { Modal } from "./Modal";
import { FichePagePicker } from "./FichePagePicker";
import { NotePad, type NotePadHandle } from "./NotePad";
import { isNotesFile } from "./notesPad";
import { printHtml, printPlain } from "./printPage";
import { kanbanOn } from "./kanbanLink";
import { DateField } from "./pickers";
import { createTask, listProperties, peekProperties, type TaskProperty } from "./tasks";
import type { CrmFiche } from "./types";

const SHEET = "crm-pop flex w-full flex-col rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]";

export type AtelierStart = "folder" | "page" | "board";

type BoardDraft = {
  title: string;
  background: string;
  columns: string[];
  source: "main" | "local";
  mainAll: boolean;
  mainKeys: string[];
  properties: BoardProperty[];
};

type PropChoice = { key: string; label: string; values: Array<{ key: string; label: string; color: string }> };

function freshBoardDraft(): BoardDraft {
  return { title: "", background: BOARD_BACKGROUNDS[0], columns: ["À faire"], source: "main", mainAll: true, mainKeys: [], properties: [] };
}

function keptProperties(properties: BoardProperty[]): BoardProperty[] {
  return properties.flatMap((prop) => {
    const label = prop.label.trim();
    if (!label) return [];
    const values = prop.values.flatMap((value) => {
      const name = value.label.trim();
      if (!name) return [];
      return [{ ...value, label: name }];
    });
    return [{ ...prop, label, values }];
  });
}

export function boardChoices(board: BoardFile, catalog: TaskProperty[]): PropChoice[] {
  if (board.source === "local") return board.properties.map((prop) => ({ key: prop.key, label: prop.label, values: prop.values }));
  const picked = board.mainAll ? catalog : catalog.filter((prop) => board.mainKeys.includes(prop.key));
  return picked.map((prop) => ({
    key: prop.key,
    label: prop.label,
    values: prop.values.map((value) => ({ key: value.key, label: value.label, color: value.color || "#64748b" })),
  }));
}

export function CoffreCreateButtons({ disabled, onCreate }: { disabled: boolean; onCreate: (kind: AtelierStart) => void }) {
  const item = "crm-pill rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none disabled:cursor-not-allowed disabled:opacity-40";
  return (
    <span className="flex shrink-0 items-center gap-1">
      <button type="button" className={item} disabled={disabled} onClick={() => onCreate("page")}>+ page</button>
      <button type="button" className={item} disabled={disabled} onClick={() => onCreate("folder")}>+ dossier</button>
      <button type="button" className={item} disabled={disabled} onClick={() => onCreate("board")}>+ kanban</button>
    </span>
  );
}

export function Atelier({ fiche, onClose, start = null }: { fiche: CrmFiche; onClose: () => void; start?: AtelierStart | null }) {
  const ownerId = fiche.coffre_owner_id || 0;
  const root = fiche.coffre_path;
  const [path, setPath] = useState(root);
  const [entries, setEntries] = useState<CoffreEntry[]>([]);
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(false);
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState(true);
  useCoffreUnlocked(() => setReload((value) => value + 1));
  const [menu, setMenu] = useState(false);
  const [draft, setDraft] = useState<AtelierStart | null>(start);
  const [name, setName] = useState("");
  const [boardDraft, setBoardDraft] = useState<BoardDraft | null>(start === "board" ? freshBoardDraft() : null);
  const [catalog, setCatalog] = useState<TaskProperty[]>([]);
  const [doc, setDoc] = useState<CoffreEntry | null>(null);
  const [pending, setPending] = useState<CoffreEntry | null>(null);

  useEffect(() => {
    setPath(root);
  }, [root]);

  useEffect(() => {
    if (!path) {
      setEntries([]);
      setBusy(false);
      return;
    }
    let alive = true;
    setBusy(true);
    setError("");
    void listCoffre(path, false, ownerId)
      .then((rows) => {
        if (!alive) return;
        setEntries(rows);
        setLocked(false);
      })
      .catch((reason: unknown) => {
        if (!alive) return;
        setLocked(coffreLocked(reason));
        setError(reason instanceof Error ? reason.message : "Dossier illisible.");
      })
      .finally(() => {
        if (alive) setBusy(false);
      });
    return () => {
      alive = false;
    };
  }, [path, ownerId, reload]);

  useEffect(() => {
    if (draft !== "board") return;
    let alive = true;
    void listProperties()
      .then((rows) => {
        if (alive) setCatalog(rows.filter((prop) => prop.key !== "client" && prop.key !== "assignedTo"));
      })
      .catch(() => {
        if (alive) setCatalog([]);
      });
    return () => {
      alive = false;
    };
  }, [draft]);

  function openDraft(kind: AtelierStart) {
    setMenu(false);
    setName("");
    setPending(null);
    setDraft(kind);
    setBoardDraft(kind === "board" ? freshBoardDraft() : null);
    setError("");
  }

  async function refresh() {
    setEntries(await listCoffre(path, false, ownerId));
  }

  async function createItem() {
    const label = name.trim();
    if (!draft || draft === "board" || !label || !path) return;
    setBusy(true);
    setError("");
    try {
      if (draft === "folder") await createCoffreFolder(path, label, ownerId);
      else await createCoffreMarkdown(path, label, "", ownerId);
      setDraft(null);
      setName("");
      setMenu(false);
      await refresh();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Création impossible.");
    } finally {
      setBusy(false);
    }
  }

  async function createBoard() {
    if (!boardDraft || !path) return;
    const title = boardDraft.title.trim();
    const columns = boardDraft.columns.map((column) => column.trim()).filter(Boolean);
    if (!title) return;
    if (columns.length === 0) {
      setError("Ajoute au moins une colonne.");
      return;
    }
    const board: BoardFile = {
      background: boardDraft.background,
      subtitle: "",
      layout: "columns",
      columns: columns.map((label) => ({ id: freshId("col"), title: label })),
      cards: [],
      source: boardDraft.source,
      mainAll: boardDraft.source === "main" ? boardDraft.mainAll : false,
      mainKeys: boardDraft.source === "main" && !boardDraft.mainAll ? boardDraft.mainKeys : [],
      properties: boardDraft.source === "local" ? keptProperties(boardDraft.properties) : [],
      notify: [],
    };
    setBusy(true);
    setError("");
    try {
      await createCoffreMarkdown(path, `${title}.board`, renderBoard(board), ownerId);
      setDraft(null);
      setBoardDraft(null);
      setMenu(false);
      await refresh();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Création impossible.");
    } finally {
      setBusy(false);
    }
  }

  async function duplicate(entry: CoffreEntry) {
    setError("");
    setBusy(true);
    try {
      await copyVaultFile(entry.path, entry.name, ownerId);
      await refresh();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Duplication impossible.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(entry: CoffreEntry) {
    setError("");
    setBusy(true);
    try {
      if (entry.kind !== "folder" && entry.name.toLowerCase().endsWith(".md") && !isBoardName(entry.name)) await deletePageAndMedia(entry.path, ownerId);
      else await deleteCoffreItem(entry.path, ownerId);
      setPending(null);
      await refresh();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Suppression impossible.");
    } finally {
      setBusy(false);
    }
  }

  const editingBoard = doc ? isBoardName(doc.name) : false;
  const sheet = doc
    ? editingBoard
      ? `${SHEET} max-h-[90vh] max-w-6xl overflow-hidden`
      : `${SHEET} h-[82vh] max-w-6xl overflow-visible`
    : draft === "board"
      ? `${SHEET} max-h-[90vh] max-w-lg overflow-hidden`
      : `${SHEET} max-w-md overflow-hidden`;
  const crumbs = !root || path === root ? [] : path.slice(root.length).replace(/^\//, "").split("/").filter(Boolean);
  const shown = entries
    .filter((entry) => entry.name.toLowerCase() !== "notes-crm.md")
    .filter((entry) => entry.kind === "folder" || entry.name.toLowerCase().endsWith(".md"))
    .sort((a, b) => Number(b.kind === "folder") - Number(a.kind === "folder") || itemTitle(a.name).localeCompare(itemTitle(b.name), "fr"));
  return (
    <Modal onClose={onClose} sheetClass={sheet}>
      {() => (
        <div className={doc || draft === "board" ? "flex h-full min-h-0 flex-1 flex-col" : "flex flex-col"}>
          <header className="mb-3 flex shrink-0 items-center gap-2">
            <button type="button" className="text-sm font-semibold" onClick={() => { setDoc(null); if (root) setPath(root); }}>
              {root.split("/").pop() || "CRM clients"}
            </button>
            {crumbs.map((crumb, index) => {
              const next = `${root}/${crumbs.slice(0, index + 1).join("/")}`;
              return (
                <span key={next} className="flex min-w-0 items-center gap-2 text-sm">
                  <span aria-hidden>/</span>
                  <button type="button" className="truncate" onClick={() => { setDoc(null); setPath(next); }}>{crumb}</button>
                </span>
              );
            })}
          </header>
          {locked ? <CoffreLockedLine className="mb-2 shrink-0 text-sm text-slate-600" /> : error ? <p className="mb-2 shrink-0 text-sm text-rose-700">{error}</p> : null}
          {!root ? (
            <p className="text-sm text-slate-500">Le dossier de la fiche dans CRM clients n’est pas prêt.</p>
          ) : doc ? (
            isBoardName(doc.name) ? (
              <BoardEditor fiche={fiche} entry={doc} onBack={() => setDoc(null)} onError={setError} onChanged={() => void refresh()} />
            ) : (
              <PageEditor entry={doc} ownerId={ownerId} onBack={() => setDoc(null)} onError={setError} onChanged={() => void refresh()} />
            )
          ) : (
            <div className={draft === "board" ? "crm-scroll min-h-0 flex-1 overflow-auto" : ""}>
              <div className="mb-3 flex shrink-0 flex-wrap items-center gap-2">
                <button type="button" className="rounded-full crm-fill px-3 py-1 text-sm font-semibold text-white" onClick={() => setMenu((open) => !open)}>+ Nouveau</button>
                {menu ? (
                  <>
                    <button type="button" className="crm-pill rounded-full px-3 py-1 text-sm" onClick={() => openDraft("folder")}>Dossier</button>
                    <button type="button" className="crm-pill rounded-full px-3 py-1 text-sm" onClick={() => openDraft("page")}>Page</button>
                    <button type="button" className="crm-pill rounded-full px-3 py-1 text-sm" onClick={() => openDraft("board")}>Kanban</button>
                  </>
                ) : null}
                {draft === "folder" || draft === "page" ? (
                  <form className="flex w-full items-center gap-2" onSubmit={(event) => { event.preventDefault(); void createItem(); }}>
                    <input className="min-w-0 flex-1 rounded-full border border-slate-200 px-3 py-1 text-sm" value={name} placeholder={draft === "folder" ? "Nom du dossier" : "Nom de la page"} onChange={(event) => setName(event.target.value)} />
                    <button type="submit" className="rounded-full crm-fill px-3 py-1 text-sm font-semibold text-white disabled:opacity-50" disabled={busy || name.trim() === ""}>Créer</button>
                  </form>
                ) : null}
              </div>
              {draft === "board" && boardDraft ? (
                <BoardComposer draft={boardDraft} catalog={catalog} busy={busy} onChange={setBoardDraft} onCreate={() => void createBoard()} onCancel={() => { setDraft(null); setBoardDraft(null); }} />
              ) : (
                <>
                  {pending ? (
                    <ConfirmBar
                      text={pending.kind === "folder" ? `Retirer le dossier « ${itemTitle(pending.name)} » ? Son contenu part dans la corbeille du Coffre.` : pending.name.toLowerCase().endsWith(".md") && !isBoardName(pending.name) ? `Supprimer « ${itemTitle(pending.name)} » ? La page part dans la corbeille, ainsi que les photos et fichiers qu’elle est seule à utiliser.` : `Supprimer « ${itemTitle(pending.name)} » ? Le fichier part dans la corbeille du Coffre.`}
                      yes={pending.kind === "folder" ? "Retirer" : "Supprimer"}
                      busy={busy}
                      onCancel={() => setPending(null)}
                      onYes={() => void remove(pending)}
                    />
                  ) : null}
                  <div className="crm-scroll max-h-[60vh] overflow-auto">
                    {busy && shown.length === 0 ? <p className="text-sm text-slate-500">Chargement…</p> : null}
                    {!busy && shown.length === 0 ? <p className="text-sm text-slate-500">Dossier vide. Ajoute un dossier, une page ou un kanban.</p> : null}
                    <ul className="grid gap-2">
                      {shown.map((entry) => (
                        <li key={entry.path} className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2">
                          <button type="button" className="min-w-0 flex-1 truncate text-left text-sm font-medium" onClick={() => (entry.kind === "folder" ? setPath(entry.path) : setDoc(entry))}>
                            <span className="mr-2 text-xs text-slate-400">{entry.kind === "folder" ? "Dossier" : isBoardName(entry.name) ? "Kanban" : "Page"}</span>
                            {itemTitle(entry.name)}
                          </button>
                          {entry.kind === "folder" || isNotesFile(entry.name) ? null : <button type="button" className="text-xs text-slate-400" disabled={busy} onClick={() => void duplicate(entry)}>Dupliquer</button>}
                          {isNotesFile(entry.name) || ownerId > 0 ? null : <button type="button" className="text-xs text-slate-400" onClick={() => setPending(entry)}>{entry.kind === "folder" ? "Retirer" : "Supprimer"}</button>}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function BoardComposer({ draft, catalog, busy, onChange, onCreate, onCancel }: { draft: BoardDraft; catalog: TaskProperty[]; busy: boolean; onChange: (next: BoardDraft) => void; onCreate: () => void; onCancel: () => void }) {
  const columnsReady = draft.columns.some((column) => column.trim() !== "");
  return (
    <form className="grid gap-3" onSubmit={(event) => { event.preventDefault(); onCreate(); }}>
      <label className="grid gap-1 text-sm">
        <span className="font-semibold">Titre</span>
        <input className="rounded-xl border border-slate-200 px-3 py-2" value={draft.title} placeholder="Nom du kanban" onChange={(event) => onChange({ ...draft, title: event.target.value })} />
      </label>
      <div className="grid gap-1">
        <span className="text-sm font-semibold">Fond du titre</span>
        <Swatches value={draft.background} colors={BOARD_BACKGROUNDS} onPick={(background) => onChange({ ...draft, background })} />
        <header className="mt-1 overflow-hidden rounded-2xl" style={{ background: draft.background }}>
          <h1 className="truncate px-5 py-4 text-left text-4xl font-semibold tracking-tight">{draft.title.trim() || "Titre"}</h1>
        </header>
      </div>
      <div className="grid gap-1">
        <span className="text-sm font-semibold">Colonnes</span>
        {draft.columns.map((column, index) => (
          <div key={index} className="flex items-center gap-2">
            <input className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-1 text-sm" value={column} placeholder="Nom de la colonne" onChange={(event) => { const columns = draft.columns.slice(); columns[index] = event.target.value; onChange({ ...draft, columns }); }} />
            <button type="button" className="text-xs text-slate-400 disabled:opacity-30" disabled={draft.columns.length === 1} onClick={() => onChange({ ...draft, columns: draft.columns.filter((_, at) => at !== index) })}>×</button>
          </div>
        ))}
        <button type="button" className="text-left text-xs font-semibold text-slate-500" onClick={() => onChange({ ...draft, columns: [...draft.columns, ""] })}>+ colonne</button>
      </div>
      <PropertySource
        source={draft.source}
        catalog={catalog}
        mainAll={draft.mainAll}
        mainKeys={draft.mainKeys}
        properties={draft.properties}
        onSource={(source) => onChange({ ...draft, source })}
        onMain={(mainAll, mainKeys) => onChange({ ...draft, mainAll, mainKeys })}
        onProperties={(properties) => onChange({ ...draft, properties })}
      />
      <div className="flex gap-2">
        <button type="submit" className="rounded-full crm-fill px-3 py-1 text-sm font-semibold text-white disabled:opacity-50" disabled={busy || draft.title.trim() === "" || !columnsReady}>Créer le kanban</button>
        <button type="button" className="crm-pill rounded-full px-3 py-1 text-sm" onClick={onCancel}>Annuler</button>
      </div>
    </form>
  );
}

function PropertySource({
  source,
  catalog,
  mainAll,
  mainKeys,
  properties,
  onSource,
  onMain,
  onProperties,
}: {
  source: "main" | "local";
  catalog: TaskProperty[];
  mainAll: boolean;
  mainKeys: string[];
  properties: BoardProperty[];
  onSource: (source: "main" | "local") => void;
  onMain: (mainAll: boolean, mainKeys: string[]) => void;
  onProperties: (properties: BoardProperty[]) => void;
}) {
  return (
    <div className="grid gap-2">
      <span className="text-sm font-semibold">Propriétés</span>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={source === "main" ? "rounded-full crm-fill px-3 py-1 text-xs font-semibold text-white" : "crm-pill rounded-full px-3 py-1 text-xs"} onClick={() => onSource("main")}>Kanban principal</button>
        <button type="button" className={source === "local" ? "rounded-full crm-fill px-3 py-1 text-xs font-semibold text-white" : "crm-pill rounded-full px-3 py-1 text-xs"} onClick={() => onSource("local")}>Ce kanban</button>
      </div>
      {source === "main" ? <MainProperties catalog={catalog} mainAll={mainAll} mainKeys={mainKeys} onChange={onMain} /> : <LocalProperties properties={properties} onChange={onProperties} />}
      <p className="text-xs text-slate-500">{source === "local" ? "Couleur comprise : chaque valeur a la sienne. Le titre s’enregistre tout de suite, fermer garde la propriété." : "Les cartes affichent les propriétés choisies du kanban principal, avec leurs couleurs. Le choix s’enregistre tout de suite."}</p>
    </div>
  );
}

function MainProperties({ catalog, mainAll, mainKeys, onChange }: { catalog: TaskProperty[]; mainAll: boolean; mainKeys: string[]; onChange: (mainAll: boolean, mainKeys: string[]) => void }) {
  const keys = mainAll ? catalog.map((prop) => prop.key) : mainKeys;
  if (catalog.length === 0) return <p className="text-xs text-slate-500">Le kanban principal n’a pas de propriété à reprendre.</p>;
  return (
    <div className="grid gap-1">
      {catalog.map((prop) => (
        <label key={prop.key} className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={keys.includes(prop.key)}
            onChange={() => {
              const next = keys.includes(prop.key) ? keys.filter((key) => key !== prop.key) : [...keys, prop.key];
              onChange(next.length === catalog.length, next);
            }}
          />
          {prop.label}
        </label>
      ))}
    </div>
  );
}

function LocalProperties({ properties, onChange }: { properties: BoardProperty[]; onChange: (next: BoardProperty[]) => void }) {
  function patch(key: string, next: BoardProperty) {
    onChange(properties.map((prop) => (prop.key === key ? next : prop)));
  }
  return (
    <div className="flex flex-wrap items-start gap-3">
      {properties.map((prop) => (
        <section key={prop.key} className="w-64 shrink-0 rounded-2xl border border-slate-200 p-3">
          <div className="flex items-center gap-1">
            <input className="min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none" value={prop.label} placeholder="Titre" onChange={(event) => patch(prop.key, { ...prop, label: event.target.value })} />
            <button type="button" className="text-xs text-slate-400" aria-label="Retirer la propriété" onClick={() => onChange(properties.filter((item) => item.key !== prop.key))}>×</button>
          </div>
          <div className="mt-2 grid gap-2">
            {prop.values.map((value) => (
              <div key={value.key} className="rounded-xl border border-slate-200 p-2">
                <div className="flex items-center gap-1">
                  <input className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2 py-1 text-xs" value={value.label} placeholder="Valeur" onChange={(event) => patch(prop.key, { ...prop, values: prop.values.map((item) => item.key === value.key ? { ...item, label: event.target.value } : item) })} />
                  <button type="button" className="text-xs text-slate-400" aria-label="Retirer la valeur" onClick={() => patch(prop.key, { ...prop, values: prop.values.filter((item) => item.key !== value.key) })}>×</button>
                </div>
                <div className="mt-2">
                  <Swatches value={value.color} colors={BOARD_SWATCHES} small onPick={(color) => patch(prop.key, { ...prop, values: prop.values.map((item) => item.key === value.key ? { ...item, color } : item) })} />
                </div>
              </div>
            ))}
          </div>
          <button type="button" className="crm-pill mt-2 rounded-full px-2 py-1 text-xs font-semibold" onClick={() => patch(prop.key, { ...prop, values: [...prop.values, { key: freshId("val"), label: "", color: BOARD_SWATCHES[prop.values.length % BOARD_SWATCHES.length] }] })}>+ valeur</button>
        </section>
      ))}
      <button type="button" className="crm-fill shrink-0 self-start rounded-full px-3 py-1.5 text-sm font-semibold" onClick={() => onChange([...properties, { key: freshId("prop"), label: "", values: [{ key: freshId("val"), label: "", color: BOARD_SWATCHES[0] }] }])}>+ Propriété</button>
    </div>
  );
}

function Swatches({ value, colors, onPick, small = false }: { value: string; colors: string[]; onPick: (color: string) => void; small?: boolean }) {
  const size = small ? "h-4 w-4" : "h-5 w-5";
  return (
    <span className={small ? "flex h-4 items-center gap-1" : "flex h-5 items-center gap-1"}>
      {colors.map((color) => (
        <button key={color} type="button" className={value === color ? `crm-board-swatch ${size} rounded-full ring-2 ring-slate-900 ring-offset-1` : `crm-board-swatch ${size} rounded-full border border-black/10`} style={{ background: color }} aria-label={color} aria-pressed={value === color} onClick={() => onPick(color)} />
      ))}
    </span>
  );
}

function ConfirmBar({ text, yes = "Retirer", busy, onCancel, onYes }: { text: string; yes?: string; busy?: boolean; onCancel: () => void; onYes: () => void }) {
  return (
    <div className="crm-board-ask mb-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-950">
      <p>{text}</p>
      <div className="mt-2 flex justify-end gap-2">
        <button type="button" className="rounded-full border border-rose-200 px-3 py-1 text-xs font-semibold" onClick={onCancel}>Annuler</button>
        <button type="button" className="rounded-full bg-rose-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50" disabled={busy} onClick={onYes}>{yes}</button>
      </div>
    </div>
  );
}

function PageEditor({ entry, ownerId, onBack, onError, onChanged }: { entry: CoffreEntry; ownerId: number; onBack: () => void; onError: (message: string) => void; onChanged: () => void }) {
  const [initial, setInitial] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [html, setHtml] = useState("");
  const [busy, setBusy] = useState(false);
  const [askDrop, setAskDrop] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    void readCoffreText(entry.path, ownerId)
      .then((raw) => {
        if (!alive) return;
        setInitial(raw);
        setText(raw);
      })
      .catch((reason: unknown) => onError(reason instanceof Error ? reason.message : "Page illisible."));
    return () => {
      alive = false;
    };
  }, [entry.path, ownerId, onError]);

  async function save() {
    setBusy(true);
    try {
      await saveCoffreText(entry.path, text, ownerId);
    } catch (reason: unknown) {
      onError(reason instanceof Error ? reason.message : "Enregistrement impossible.");
    } finally {
      setBusy(false);
    }
  }

  async function duplicate() {
    setBusy(true);
    setCopied(false);
    try {
      await copyVaultFile(entry.path, entry.name, ownerId);
      onChanged();
      setCopied(true);
    } catch (reason: unknown) {
      onError(reason instanceof Error ? reason.message : "Duplication impossible.");
    } finally {
      setBusy(false);
    }
  }

  async function drop() {
    setBusy(true);
    try {
      await deletePageAndMedia(entry.path, ownerId);
      onChanged();
      onBack();
    } catch (reason: unknown) {
      onError(reason instanceof Error ? reason.message : "Suppression impossible.");
    } finally {
      setBusy(false);
    }
  }

  function print() {
    try {
      if (html.trim()) printHtml(itemTitle(entry.name), html);
      else printPlain(itemTitle(entry.name), text.replace(/\n*<!--BLOCKS_JSON[\s\S]*$/, "").trim());
    } catch (reason: unknown) {
      onError(reason instanceof Error ? reason.message : "Impression impossible.");
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-3 flex shrink-0 items-center gap-2">
        <button type="button" className="crm-pill rounded-full px-3 py-1 text-sm" onClick={onBack}>Retour</button>
        <p className="min-w-0 truncate text-sm font-semibold">{itemTitle(entry.name)}</p>
        {isNotesFile(entry.name) ? null : <button type="button" className="ml-auto crm-pill rounded-full px-3 py-1 text-sm" disabled={busy} onClick={() => void duplicate()}>Dupliquer</button>}
        {isNotesFile(entry.name) || ownerId > 0 ? null : <button type="button" className="crm-pill rounded-full px-3 py-1 text-sm" disabled={busy} onClick={() => setAskDrop(true)}>Supprimer</button>}
        <button type="button" className="crm-pill rounded-full px-3 py-1 text-sm" onClick={print}>Imprimer</button>
        <button type="button" className="rounded-full crm-fill px-3 py-1 text-sm font-semibold text-white disabled:opacity-50" disabled={busy || initial === null} onClick={() => void save()}>Enregistrer</button>
      </div>
      {copied ? <p className="mb-2 shrink-0 text-xs text-slate-500">Copie créée à côté de cette page.</p> : null}
      {askDrop ? <ConfirmBar text={`Supprimer « ${itemTitle(entry.name)} » ? La page part dans la corbeille, ainsi que les photos et fichiers qu’elle est seule à utiliser.`} yes="Supprimer" busy={busy} onCancel={() => setAskDrop(false)} onYes={() => void drop()} /> : null}
      <div className="crm-notepad crm-notepad-page min-h-0 flex-1 rounded-2xl border border-slate-200 p-3">
        {initial !== null ? <NotePad key={entry.path} initial={initial} portaled upload={{ folder: entry.path.slice(0, Math.max(0, entry.path.lastIndexOf("/"))), ownerId, pagePath: entry.path }} persist={async (value) => { await saveCoffreText(entry.path, value, ownerId); }} onChange={setText} onHtml={setHtml} /> : <p className="text-sm text-slate-500">Chargement…</p>}
      </div>
    </div>
  );
}

function boardProperty(prop: TaskProperty): boolean {
  return prop.key !== "client" && prop.key !== "assignedTo";
}

export function BoardEditor({ fiche, entry, onBack, onError, notice = "", onChanged, frame }: { fiche: CrmFiche; entry: CoffreEntry; onBack: () => void; onError: (message: string) => void; notice?: string; onChanged?: () => void; frame?: { full: boolean; onFull: () => void } }) {
  const ownerId = fiche.coffre_owner_id || 0;
  const [board, setBoard] = useState<BoardFile | null>(null);
  const [catalog, setCatalog] = useState<TaskProperty[]>([]);
  const [busy, setBusy] = useState(false);
  const [postId, setPostId] = useState("");
  const [noteId, setNoteId] = useState("");
  const [sendError, setSendError] = useState("");
  const [columnName, setColumnName] = useState("");
  const [tune, setTune] = useState(false);
  const [ask, setAsk] = useState<{ kind: "column" | "card"; id: string; label: string } | null>(null);
  const [fileAsk, setFileAsk] = useState(false);
  const [fileMenu, setFileMenu] = useState<{ x: number; y: number } | null>(null);
  const [copied, setCopied] = useState(false);
  const [dragId, setDragId] = useState("");
  const [over, setOver] = useState<{ columnId: string; beforeId: string } | null>(null);
  const [hint, setHint] = useState("");
  const [veil, setVeil] = useState<{ phase: "send" | "done"; label: string; detail: string } | null>(null);
  const veilTimer = useRef(0);
  const live = useRef<BoardFile | null>(null);
  const flight = useRef<{ id: string; left: number; top: number } | null>(null);
  live.current = board;

  useEffect(() => () => window.clearTimeout(veilTimer.current), []);

  function holdResult(label: string) {
    setVeil({ phase: "done", label: label || "Envoyé.", detail: "" });
    window.clearTimeout(veilTimer.current);
    veilTimer.current = window.setTimeout(() => setVeil(null), 1700);
  }

  useEffect(() => {
    window.dispatchEvent(new Event("crm-board-opened"));
    return () => {
      void flushBoardWrites().then(() => {
        window.dispatchEvent(new Event("crm-board-closed"));
      });
    };
  }, [entry.path]);

  function paint(next: BoardFile) {
    live.current = next;
    setBoard(next);
  }

  useEffect(() => {
    if (!fileMenu) return;
    function close() {
      setFileMenu(null);
    }
    window.addEventListener("pointerdown", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("pointerdown", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [fileMenu]);

  function openFileMenu(event: ReactMouseEvent) {
    if (ownerId > 0) return;
    const target = event.target;
    if (target instanceof Element && target.closest("input, textarea, button, a, select, [data-card-id]")) return;
    event.preventDefault();
    setFileMenu({ x: event.clientX, y: event.clientY });
  }

  useEffect(() => {
    let alive = true;
    const known = peekProperties();
    if (known) setCatalog(known.filter(boardProperty));
    void readCoffreText(entry.path, ownerId)
      .then((raw) => {
        if (!alive) return;
        const parsed = parseBoard(raw);
        if (!isPipeBoard(entry.name)) {
          setBoard(parsed);
          return;
        }
        const plain = plainSuivi(parsed);
        setBoard(plain);
        if (suiviDirty(parsed)) {
          void enqueueBoardWrite(async () => {
            const current = live.current;
            if (!current) return;
            await saveCoffreText(entry.path, renderBoard(plainSuivi(current)), ownerId);
          }).catch(() => undefined);
        }
      })
      .catch((reason: unknown) => onError(reason instanceof Error ? reason.message : "Kanban illisible."));
    void listProperties()
      .then((props) => {
        if (alive) setCatalog(props.filter(boardProperty));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [entry.path, ownerId, onError]);

  async function save(next: BoardFile) {
    paint(next);
    await enqueueBoardWrite(async () => {
      setBusy(true);
      try {
        await saveCoffreText(entry.path, renderBoard(next), ownerId);
      } catch (reason: unknown) {
        onError(reason instanceof Error ? reason.message : "Enregistrement impossible.");
      } finally {
        setBusy(false);
      }
    });
  }

  async function commitMove(previous: BoardFile, next: BoardFile) {
    const card = columnChanged(previous, next);
    paint(next);
    let stamped = next;
    if (!isPipeBoard(entry.name) && card && watchActive(next.notify)) {
      setHint("");
      setVeil({ phase: "send", ...sendVeilCopy(next.notify, card.title) });
      setBusy(true);
      try {
        const result = await deliverNotice(fiche, itemTitle(entry.name), next, card, "move");
        stamped = result.board;
        setHint(result.hint);
        holdResult(result.hint);
      } catch (reason: unknown) {
        const message = reason instanceof Error ? reason.message : "Notification impossible.";
        onError(message);
        holdResult(message);
      }
    }
    await save(stamped);
  }

  async function ping(cardId: string) {
    if (isPipeBoard(entry.name)) return;
    const current = live.current;
    if (!current) return;
    const card = current.cards.find((item) => item.id === cardId);
    if (!card) return;
    setHint("");
    setVeil({ phase: "send", ...sendVeilCopy(current.notify, card.title) });
    setBusy(true);
    try {
      const result = await deliverNotice(fiche, itemTitle(entry.name), current, card, "manual");
      setHint(result.hint);
      holdResult(result.hint);
      if (result.board !== current) await save(result.board);
      else setBusy(false);
    } catch (reason: unknown) {
      const message = reason instanceof Error ? reason.message : "Notification impossible.";
      onError(message);
      holdResult(message);
      setBusy(false);
    }
  }

  async function send(cardId: string, day: string, mode: "copy" | "move"): Promise<boolean> {
    const current = live.current;
    if (!current || !day) return false;
    const card = current.cards.find((item) => item.id === cardId);
    if (!card || card.title.trim() === "") return false;
    const choices = boardChoices(current, catalog);
    setBusy(true);
    setSendError("");
    try {
      const props = Object.fromEntries(Object.entries(card.props).filter(([, value]) => value));
      const boardProps = choices.flatMap((prop) => {
        const chosen = prop.values.find((value) => value.key === card.props[prop.key]);
        if (!chosen || !chosen.label.trim()) return [];
        return [{ label: prop.label, value: chosen.label, color: chosen.color || "#64748b" }];
      });
      await createTask({
        title: card.title.trim(),
        day,
        props,
        description: card.note,
        fiche: { id: fiche.id, name: fiche.name },
        board: itemTitle(entry.name),
        boardProps,
      });
      if (mode === "move") {
        const fresh = live.current;
        if (!fresh) return true;
        await save({ ...fresh, cards: fresh.cards.filter((item) => item.id !== cardId) });
      }
      return true;
    } catch (reason: unknown) {
      setSendError(reason instanceof Error ? reason.message : "Envoi impossible.");
      return false;
    } finally {
      setBusy(false);
    }
  }

  function advance(cardId: string) {
    const current = live.current;
    if (!current) return;
    const card = current.cards.find((item) => item.id === cardId);
    if (!card) return;
    const index = current.columns.findIndex((column) => column.id === card.columnId);
    const next = current.columns[index + 1];
    if (!next) return;
    const node = document.querySelector(`[data-card-id="${cardId}"]`);
    if (node instanceof HTMLElement) {
      const box = node.getBoundingClientRect();
      flight.current = { id: cardId, left: box.left, top: box.top };
    }
    const cards = placeCard(current.cards, card, next.id, "");
    if (sameOrder(current.cards, cards)) {
      flight.current = null;
      return;
    }
    void commitMove(current, { ...current, cards });
  }

  useLayoutEffect(() => {
    const shot = flight.current;
    if (!shot) return;
    flight.current = null;
    const node = document.querySelector(`[data-card-id="${shot.id}"]`);
    if (!(node instanceof HTMLElement)) return;
    const last = node.getBoundingClientRect();
    const dx = shot.left - last.left;
    const dy = shot.top - last.top;
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    node.animate(
      [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "translate(0, 0)" }],
      { duration: 460, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
    );
  }, [board]);

  async function duplicateFile() {
    setBusy(true);
    setCopied(false);
    try {
      await copyVaultFile(entry.path, entry.name, ownerId);
      onChanged?.();
      setCopied(true);
    } catch (reason: unknown) {
      onError(reason instanceof Error ? reason.message : "Duplication impossible.");
    } finally {
      setBusy(false);
    }
  }

  async function deleteFile() {
    setBusy(true);
    try {
      await deleteCoffreItem(entry.path, ownerId);
      onChanged?.();
      onBack();
    } catch (reason: unknown) {
      onError(reason instanceof Error ? reason.message : "Suppression impossible.");
    } finally {
      setBusy(false);
    }
  }

  function confirmDrop() {
    const current = live.current;
    if (!current || !ask) return;
    if (ask.kind === "column") {
      void save({ ...current, columns: current.columns.filter((item) => item.id !== ask.id), cards: current.cards.filter((card) => card.columnId !== ask.id) });
    } else {
      void save({ ...current, cards: current.cards.filter((item) => item.id !== ask.id) });
    }
    setAsk(null);
  }

  if (!board) {
    return (
      <div className="crm-board-boot" role="status" aria-live="polite">
        {[0, 1, 2].map((column) => (
          <div key={column} className="crm-board-boot-col" aria-hidden>
            <span className="crm-mail-skel-bar w-24" />
            <span className="crm-mail-skel-bar w-full" />
            <span className="crm-mail-skel-bar w-4/5" />
            <span className="crm-mail-skel-bar w-3/5" />
          </div>
        ))}
        <div className="crm-browse-loading">
          <span className="crm-browse-loading-spin" aria-hidden />
          <span>Ouverture du kanban…</span>
        </div>
      </div>
    );
  }
  const choices = boardChoices(board, catalog);
  const askText = !ask ? "" : ask.kind === "card"
    ? `Retirer « ${ask.label} » ?`
    : `Retirer la colonne « ${ask.label} »${board.cards.some((card) => card.columnId === ask.id) ? " et ses cartes" : ""} ?`;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <header className={frame ? "crm-board-head relative flex shrink-0 items-start justify-between" : "crm-board-head relative mb-3 flex shrink-0 items-start justify-between rounded-2xl"} style={{ "--crm-board-bg": board.background } as CSSProperties} onContextMenu={openFileMenu}>
        <div className="min-w-0 flex-1 px-5 py-4">
          {frame ? null : <button type="button" className="crm-board-pill crm-pill mb-2 rounded-full bg-white/80 px-3 py-1 text-sm" onClick={onBack}>Retour</button>}
          <h1 className="truncate text-4xl font-semibold tracking-tight">{itemTitle(entry.name)}</h1>
          <input
            className="crm-board-lead mt-1 w-full bg-transparent text-base leading-snug outline-none"
            value={board.subtitle}
            placeholder="Description, ligne de bas…"
            aria-label="Description"
            onChange={(event) => { const current = live.current; if (current) paint({ ...current, subtitle: event.target.value }); }}
            onBlur={() => { if (live.current) void save(live.current); }}
            onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); event.currentTarget.blur(); } }}
          />
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 px-4 py-4">
          <Swatches value={board.background} colors={BOARD_BACKGROUNDS} onPick={(background) => { const current = live.current; if (current) void save({ ...current, background }); }} />
          {frame ? (
            <button type="button" className="crm-board-pill grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white/80 text-slate-600 hover:bg-white" aria-label={frame.full ? "Réduire" : "Plein écran"} title={frame.full ? "Réduire" : "Plein écran"} onClick={frame.onFull}>
              {frame.full ? (
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 4v4H4M15 4v4h5M9 20v-4H4M15 20v-4h5" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
                </svg>
              )}
            </button>
          ) : null}
          <button type="button" className="crm-board-pill grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white/80 text-slate-600 hover:bg-white disabled:opacity-40" aria-label="Dupliquer" title="Dupliquer" disabled={busy} onClick={() => void duplicateFile()}>
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
              <rect x="8" y="8" width="12" height="12" rx="2" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16V6a2 2 0 0 1 2-2h10" />
            </svg>
          </button>
          <button type="button" className="crm-board-pill crm-pill rounded-full bg-white/80 px-3 py-1 text-xs" onClick={() => setTune((open) => !open)}>{tune ? "Fermer" : "Réglages"}</button>
          {frame ? (
            <button type="button" className="crm-board-pill grid h-8 w-8 place-items-center rounded-full bg-white/80 text-slate-500 hover:bg-white" aria-label="Fermer" onClick={onBack}>
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
                <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          ) : null}
        </div>
        <p className="pointer-events-none absolute bottom-1.5 right-4 text-[11px] text-slate-500" aria-live="polite">{busy ? "Enregistrement…" : copied ? "Copie créée à côté." : ""}</p>
      </header>
      <div className={frame ? "flex min-h-0 flex-1 flex-col px-4 pb-4 pt-3" : "flex min-h-0 flex-1 flex-col"}>
      {notice ? <p className="mb-2 shrink-0 text-sm text-rose-700">{notice}</p> : null}
      {hint ? <p className="mb-2 shrink-0 text-sm text-slate-600">{hint}</p> : null}
      {fileAsk ? <ConfirmBar text={`Supprimer « ${itemTitle(entry.name)} » ? Le kanban part dans la corbeille du Coffre.`} yes="Supprimer" busy={busy} onCancel={() => setFileAsk(false)} onYes={() => void deleteFile()} /> : null}
      {tune ? (
        <BoardTune
          board={board}
          ficheId={fiche.id}
          plain={isPipeBoard(entry.name)}
          catalog={catalog}
          onApply={(next) => {
            const current = live.current;
            if (!current) return;
            void save({
              ...current,
              layout: next.layout,
              source: next.source,
              mainAll: next.mainAll,
              mainKeys: next.mainKeys,
              properties: next.properties,
              notify: next.notify,
            });
          }}
          onClose={() => setTune(false)}
          onNotify={(rows) => {
            const current = live.current;
            if (current) void save({ ...current, notify: rows });
          }}
        />
      ) : (
      <>
      {ask ? <ConfirmBar text={askText} busy={busy} onCancel={() => setAsk(null)} onYes={confirmDrop} /> : null}
      <div className={board.layout === "grid" ? "crm-scroll flex min-h-0 flex-1 flex-col gap-3 overflow-auto" : "crm-scroll flex min-h-0 flex-1 gap-3 overflow-auto"} onContextMenu={openFileMenu}>
        {board.columns.map((column) => (
          <section
            key={column.id}
            className={`${over?.columnId === column.id ? "crm-board-col is-over flex flex-col rounded-2xl border p-2" : "crm-board-col flex flex-col rounded-2xl border border-slate-200 p-2"} ${board.layout === "grid" ? "w-full" : "w-72 shrink-0"}`}
            onDragOver={(event) => {
              if (!dragId) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = "move";
              const node = event.target instanceof Element ? event.target.closest("[data-card-id]") : null;
              const beforeId = node?.getAttribute("data-card-id") || "";
              if (beforeId === dragId) return;
              setOver((current) => (current?.columnId === column.id && current.beforeId === beforeId ? current : { columnId: column.id, beforeId }));
            }}
            onDrop={(event) => {
              event.preventDefault();
              const current = live.current;
              const id = event.dataTransfer.getData("text/plain") || dragId;
              const node = event.target instanceof Element ? event.target.closest("[data-card-id]") : null;
              const beforeId = node?.getAttribute("data-card-id") || "";
              setDragId("");
              setOver(null);
              if (!current || !id) return;
              const card = current.cards.find((item) => item.id === id);
              if (!card) return;
              const cards = placeCard(current.cards, card, column.id, beforeId === id ? "" : beforeId);
              if (sameOrder(current.cards, cards)) return;
              void commitMove(current, { ...current, cards });
            }}
          >
            <div className="mb-2 flex items-center gap-1">
              <input className="crm-board-plain min-w-0 flex-1 bg-transparent text-sm font-semibold" value={column.title} onChange={(event) => { const current = live.current; if (!current) return; paint({ ...current, columns: current.columns.map((item) => item.id === column.id ? { ...item, title: event.target.value } : item) }); }} onBlur={() => { if (live.current) void save(live.current); }} />
              <TrashButton label="Retirer la colonne" disabled={board.columns.length === 1} onClick={() => setAsk({ kind: "column", id: column.id, label: column.title || "colonne" })} />
            </div>
            <ul className={board.layout === "grid" ? "crm-board-cards is-row" : "space-y-2"}>
              {board.cards.filter((card) => card.columnId === column.id).map((card) => (
                <CardEditor key={card.id} fiche={fiche} card={card} choices={choices} busy={busy} alerts={!isPipeBoard(entry.name)} canNotify={watchActive(board.notify)} noteOpen={noteId === card.id} upload={fiche.coffre_path ? { folder: fiche.coffre_path, ownerId: fiche.coffre_owner_id || 0 } : null} nextTitle={board.columns[board.columns.findIndex((item) => item.id === column.id) + 1]?.title || ""} dragging={dragId === card.id} landing={over?.columnId === column.id && over.beforeId === card.id} onChange={(patch) => { const current = live.current; if (!current) return; paint({ ...current, cards: current.cards.map((item) => item.id === card.id ? { ...item, ...patch } : item) }); }} onBlur={() => { if (live.current) void save(live.current); }} onNotify={() => void ping(card.id)} onSend={() => { setSendError(""); setPostId(card.id); }} onNote={() => setNoteId(card.id)} onNoteClose={() => setNoteId("")} onAdvance={() => advance(card.id)} onRemove={() => setAsk({ kind: "card", id: card.id, label: card.title.trim() || "cette carte" })} onDragStart={(event) => { event.dataTransfer.effectAllowed = "move"; event.dataTransfer.setData("text/plain", card.id); setDragId(card.id); }} onDragEnd={() => { setDragId(""); setOver(null); }} />
              ))}
            </ul>
            <CardComposer onAdd={(title) => { const current = live.current; if (!current) return; void save({ ...current, cards: [...current.cards, blankCard(column.id, title)] }); }} />
          </section>
        ))}
        <form className="w-56 shrink-0" onSubmit={(event) => { event.preventDefault(); if (!columnName.trim()) return; void save({ ...board, columns: [...board.columns, { id: freshId("col"), title: columnName.trim() }] }); setColumnName(""); }}>
          <input className="crm-board-newcol w-full rounded-full border border-slate-200 px-3 py-1 text-sm" value={columnName} placeholder="Nouvelle colonne" onChange={(event) => setColumnName(event.target.value)} />
        </form>
      </div>
      </>
      )}
      </div>
      {postId && board.cards.some((card) => card.id === postId) ? (
        <SendCard
          card={board.cards.find((card) => card.id === postId)!}
          column={board.columns.find((column) => column.id === board.cards.find((card) => card.id === postId)?.columnId)?.title || ""}
          boardName={itemTitle(entry.name)}
          ficheName={fiche.name}
          choices={choices}
          busy={busy}
          error={sendError}
          onClose={() => { if (!busy) setPostId(""); }}
          onSend={async (day, mode) => {
            const ok = await send(postId, day, mode);
            if (ok) setPostId("");
          }}
        />
      ) : null}
      {veil ? <SendVeil phase={veil.phase} label={veil.label} detail={veil.detail} /> : null}
      {fileMenu && ownerId === 0 ? createPortal(
        <div className="crm-board-menu fixed z-[80] min-w-36 rounded-xl border border-slate-200 p-1 text-sm shadow-xl" style={{ left: Math.min(fileMenu.x, window.innerWidth - 170), top: Math.min(fileMenu.y, window.innerHeight - 80) }} onPointerDown={(event) => event.stopPropagation()}>
          <button type="button" className="block w-full rounded-lg px-3 py-1.5 text-left font-semibold text-rose-700 hover:bg-rose-50" onClick={() => { setFileMenu(null); setFileAsk(true); }}>Supprimer</button>
        </div>,
        document.body,
      ) : null}
    </div>
  );
}

function BoardTune({ board, ficheId, plain = false, catalog, onApply, onClose, onNotify }: { board: BoardFile; ficheId: number; plain?: boolean; catalog: TaskProperty[]; onApply: (next: BoardFile) => void; onClose: () => void; onNotify: (rows: BoardWatch[]) => void }) {
  const [source, setSource] = useState(board.source);
  const [mainAll, setMainAll] = useState(board.mainAll);
  const [mainKeys, setMainKeys] = useState(board.mainKeys);
  const [properties, setProperties] = useState(board.properties);
  const [layout, setLayout] = useState(board.layout);
  const [notify, setNotify] = useState<BoardWatch[]>(board.notify);
  const [people, setPeople] = useState<NotifyPerson[]>([]);
  const [peopleReady, setPeopleReady] = useState(false);
  const [peopleNote, setPeopleNote] = useState("");
  const self = Number(currentUserId()) || 0;
  const skipSave = useRef(true);
  const notifyRef = useRef<BoardWatch[]>(board.notify);
  const publish = useRef<() => void>(() => undefined);

  function remember(rows: BoardWatch[]) {
    notifyRef.current = rows;
    setNotify(rows);
  }

  function draftBoard(): BoardFile {
    const rows = notifyRef.current;
    return {
      ...board,
      layout,
      source: plain ? "local" : source,
      mainAll: plain ? false : source === "main" ? mainAll : false,
      mainKeys: plain || source !== "main" || mainAll ? [] : mainKeys,
      properties: plain ? [] : source === "local" ? keptProperties(properties) : board.properties,
      notify: plain ? [] : peopleReady ? rows.filter((row) => people.some((person) => person.id === row.id && ((row.mail && person.mail) || (row.sms && person.sms)))) : rows,
    };
  }

  publish.current = () => onApply(draftBoard());

  useEffect(() => {
    if (skipSave.current) {
      skipSave.current = false;
      return;
    }
    const handle = window.setTimeout(() => publish.current(), 200);
    return () => window.clearTimeout(handle);
  }, [layout, source, mainAll, mainKeys, properties]);

  useEffect(() => () => publish.current(), []);

  useEffect(() => {
    if (plain) return;
    let alive = true;
    void loadNotifyPeople(ficheId)
      .then((rows) => {
        if (!alive) return;
        setPeople(rows);
        setNotify((current) => {
          const next = current.filter((row) => rows.some((person) => person.id === row.id));
          notifyRef.current = next;
          return next;
        });
        setPeopleReady(true);
      })
      .catch((reason: unknown) => {
        if (!alive) return;
        setPeopleNote(reason instanceof Error ? reason.message : "Liste indisponible.");
      });
    return () => {
      alive = false;
    };
  }, [ficheId, plain]);

  function setWatch(id: number, key: "mail" | "sms", on: boolean) {
    const current = notify.find((row) => row.id === id) || { id, mail: false, sms: false };
    const row = { ...current, [key]: on };
    const rest = notify.filter((item) => item.id !== id);
    const next = row.mail || row.sms ? [...rest, row] : rest;
    const kept = next.filter((item) => people.some((person) => person.id === item.id && ((item.mail && person.mail) || (item.sms && person.sms))));
    remember(kept);
    onNotify(kept);
  }

  return (
    <div className="crm-board-tune min-h-0 w-full flex-1 overflow-auto rounded-2xl border border-slate-200 p-3">
      <div className="mb-3">
        <p className="mb-1 text-xs font-semibold text-slate-500">Cartes</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={layout === "columns" ? "rounded-full crm-fill px-3 py-1 text-xs font-semibold text-white" : "crm-pill rounded-full px-3 py-1 text-xs font-semibold"} onClick={() => setLayout("columns")}>Colonnes</button>
          <button type="button" className={layout === "grid" ? "rounded-full crm-fill px-3 py-1 text-xs font-semibold text-white" : "crm-pill rounded-full px-3 py-1 text-xs font-semibold"} onClick={() => setLayout("grid")}>Plusieurs par ligne</button>
        </div>
        <p className="mt-1 text-xs text-slate-500">Plusieurs par ligne aligne les cartes côte à côte, chacune à la largeur de la colonne À faire.</p>
      </div>
      {plain ? null : (
        <PropertySource
          source={source}
          catalog={catalog}
          mainAll={mainAll}
          mainKeys={mainKeys}
          properties={properties}
          onSource={setSource}
          onMain={(all, keys) => { setMainAll(all); setMainKeys(keys); }}
          onProperties={setProperties}
        />
      )}
      <button
        type="button"
        className="mt-2 rounded-full crm-fill px-3 py-1 text-xs font-semibold text-white"
        onClick={() => {
          onApply(draftBoard());
          onClose();
        }}
      >
        Appliquer
      </button>
      {plain ? null : <NotifyTune people={people} notify={notify} self={self} note={peopleNote} ready={peopleReady} onWatch={setWatch} />}
    </div>
  );
}

function NotifyTune({ people, notify, self, note, ready, onWatch }: { people: NotifyPerson[]; notify: BoardWatch[]; self: number; note: string; ready: boolean; onWatch: (id: number, key: "mail" | "sms", on: boolean) => void }) {
  return (
    <div className="mt-4 border-t border-slate-200 pt-3">
      <p className="text-xs font-semibold text-slate-500">Notifications</p>
      <p className="mt-1 text-xs leading-relaxed text-slate-500">Ces personnes voient la fiche. Cocher enregistre tout de suite. Notifier et un changement de colonne envoient le mail et le SMS, y compris à toi si tu es coché. Créer une carte n’envoie rien.</p>
      {note ? <p className="mt-2 text-xs text-rose-700">{note}</p> : null}
      {!ready && !note ? <p className="mt-2 text-xs text-slate-500">Chargement…</p> : null}
      {ready && people.length === 0 && !note ? <p className="mt-2 text-xs text-slate-500">Personne à cocher pour l’instant.</p> : null}
      <ul className="mt-2 space-y-2">
        {people.map((person) => {
          const watch = notify.find((row) => row.id === person.id);
          const mark = person.name.trim().charAt(0).toLocaleUpperCase("fr") || "?";
          return (
            <li key={person.id} className="crm-notify-row flex w-fit max-w-full flex-wrap items-center gap-3 rounded-2xl px-3 py-2">
              <span className="crm-soft grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-semibold">{mark}</span>
              <span className="truncate text-sm font-semibold">
                {person.name}
                {person.id === self ? <span className="ml-1.5 text-xs font-medium text-slate-400">· toi</span> : null}
              </span>
              <span className="flex items-center gap-1.5">
                <ChannelTick label="E-mail" on={!!watch?.mail} enabled={person.mail} missing="Pas d’e-mail sur ce compte" onToggle={() => onWatch(person.id, "mail", !watch?.mail)} />
                <ChannelTick label="SMS" on={!!watch?.sms} enabled={person.sms} missing="Pas de téléphone sur ce compte" onToggle={() => onWatch(person.id, "sms", !watch?.sms)} />
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ChannelTick({ label, on, enabled, missing, onToggle }: { label: string; on: boolean; enabled: boolean; missing: string; onToggle: () => void }) {
  const lit = enabled && on;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={lit}
      disabled={!enabled}
      title={enabled ? label : missing}
      className={enabled ? (lit ? "crm-soft flex items-center gap-1.5 rounded-full py-1 pl-1.5 pr-2.5 text-xs font-semibold" : "flex items-center gap-1.5 rounded-full py-1 pl-1.5 pr-2.5 text-xs font-medium text-slate-600 hover:bg-black/5") : "flex cursor-not-allowed items-center gap-1.5 rounded-full py-1 pl-1.5 pr-2.5 text-xs text-slate-400"}
      onClick={onToggle}
    >
      <span className={lit ? "crm-tick is-on" : "crm-tick"} aria-hidden>
        {lit ? (
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12.5 10 17.5 19 7" />
          </svg>
        ) : null}
      </span>
      {label}
    </button>
  );
}

function TrashButton({ label, disabled = false, onClick }: { label: string; disabled?: boolean; onClick: () => void }) {
  return (
    <button type="button" className="crm-board-trash grid h-7 w-7 shrink-0 place-items-center rounded-lg text-rose-600 hover:bg-rose-50 disabled:opacity-30" aria-label={label} title={label} disabled={disabled} onClick={onClick}>
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
        <path strokeLinecap="round" d="M9 4.5h6" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 7.5h14" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 7.5l.6 11.2a1.4 1.4 0 0 0 1.4 1.3h5a1.4 1.4 0 0 0 1.4-1.3l.6-11.2" />
      </svg>
    </button>
  );
}

function CardComposer({ onAdd }: { onAdd: (title: string) => void }) {
  const [title, setTitle] = useState("");
  return (
    <form
      className="mt-2"
      onSubmit={(event) => {
        event.preventDefault();
        const name = title.trim();
        if (!name) return;
        onAdd(name);
        setTitle("");
      }}
    >
      <input
        className="crm-board-composer w-full rounded-xl border border-dashed border-slate-300 bg-white/70 px-2 py-1.5 text-sm"
        value={title}
        placeholder="Nouvelle tâche, puis Entrée"
        onChange={(event) => setTitle(event.target.value)}
      />
    </form>
  );
}

function placeCard(cards: BoardFile["cards"], card: BoardFile["cards"][number], columnId: string, beforeId: string): BoardFile["cards"] {
  const next = { ...card, columnId };
  const rest = cards.filter((item) => item.id !== card.id);
  if (beforeId) {
    const at = rest.findIndex((item) => item.id === beforeId && item.columnId === columnId);
    if (at >= 0) {
      rest.splice(at, 0, next);
      return rest;
    }
  }
  let last = rest.length;
  for (let index = rest.length - 1; index >= 0; index -= 1) {
    if (rest[index]?.columnId === columnId) {
      last = index + 1;
      break;
    }
  }
  if (!rest.some((item) => item.columnId === columnId)) last = rest.length;
  rest.splice(last, 0, next);
  return rest;
}

function sameOrder(before: BoardFile["cards"], after: BoardFile["cards"]): boolean {
  return before.length === after.length && before.every((card, index) => card.id === after[index]?.id && card.columnId === after[index]?.columnId);
}

function CardEditor({
  fiche,
  card,
  choices,
  busy,
  alerts = true,
  canNotify,
  noteOpen,
  upload,
  nextTitle,
  dragging,
  landing,
  onChange,
  onBlur,
  onNotify,
  onSend,
  onNote,
  onNoteClose,
  onAdvance,
  onRemove,
  onDragStart,
  onDragEnd,
}: {
  fiche: CrmFiche;
  card: { id: string; title: string; note: string; props: Record<string, string>; notice: CardNotice | null };
  choices: PropChoice[];
  busy: boolean;
  alerts?: boolean;
  canNotify: boolean;
  noteOpen: boolean;
  upload: { folder: string; ownerId: number } | null;
  nextTitle: string;
  dragging: boolean;
  landing: boolean;
  onChange: (patch: Partial<{ title: string; note: string; props: Record<string, string> }>) => void;
  onBlur: () => void;
  onNotify: () => void;
  onSend: () => void;
  onNote: () => void;
  onNoteClose: () => void;
  onAdvance: () => void;
  onRemove: () => void;
  onDragStart: (event: DragEvent<HTMLButtonElement>) => void;
  onDragEnd: () => void;
}) {
  const padRef = useRef<NotePadHandle>(null);
  const [mounted, setMounted] = useState(false);
  const [padKey, setPadKey] = useState(0);
  const shell = `${dragging ? "crm-board-card is-drag" : landing ? "crm-board-card is-land" : "crm-board-card"} overflow-hidden rounded-xl border border-slate-200 bg-white${noteOpen ? " is-note" : ""}`;

  function openNote() {
    setMounted(true);
    window.requestAnimationFrame(() => onNote());
  }

  async function saveNote() {
    let note = card.note;
    if (padRef.current) {
      try {
        const flushed = await padRef.current.flush();
        if (typeof flushed === "string") note = flushed;
      } catch {
        note = card.note;
      }
    }
    onChange({ note });
    onBlur();
  }

  async function closeNote() {
    await saveNote();
    onNoteClose();
  }

  return (
    <li data-card-id={card.id} className={shell}>
      <div className={noteOpen ? "crm-card-split is-open" : "crm-card-split"}>
        <div className="min-w-0 p-2">
          <div className="flex items-start gap-1">
            <button type="button" draggable className="crm-board-grip" aria-label="Déplacer la tâche" title="Déplacer" onDragStart={onDragStart} onDragEnd={onDragEnd}>
              <svg viewBox="0 0 12 16" className="h-4 w-3" fill="currentColor" aria-hidden>
                <circle cx="3" cy="3" r="1.2" />
                <circle cx="9" cy="3" r="1.2" />
                <circle cx="3" cy="8" r="1.2" />
                <circle cx="9" cy="8" r="1.2" />
                <circle cx="3" cy="13" r="1.2" />
                <circle cx="9" cy="13" r="1.2" />
              </svg>
            </button>
            <div className="min-w-0 flex-1">
              <input className="crm-board-plain w-full bg-transparent text-sm font-medium" value={card.title} placeholder="Titre" onChange={(event) => onChange({ title: event.target.value })} onBlur={onBlur} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); event.currentTarget.blur(); } }} />
              <input className="crm-board-plain mt-0.5 w-full bg-transparent text-xs text-slate-500" value={visibleNote(card.note)} placeholder="Description" onChange={(event) => onChange({ note: event.target.value })} onBlur={onBlur} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); event.currentTarget.blur(); } }} />
              <div className="mt-1.5 flex flex-wrap gap-1">
                {noteOpen ? null : <button type="button" className="crm-pill rounded-full px-2 py-0.5 text-[10px] font-semibold" disabled={busy} onClick={openNote}>Ouvrir</button>}
                {alerts ? <button type="button" className="crm-pill rounded-full px-2 py-0.5 text-[10px] font-semibold disabled:opacity-40" disabled={busy || !canNotify || card.title.trim() === ""} title={canNotify ? "Envoyer maintenant aux personnes cochées, y compris toi" : "Coche quelqu’un dans Réglages"} onClick={onNotify}>Notifier</button> : null}
                <button type="button" className="rounded-full crm-fill px-2 py-0.5 text-[10px] font-semibold text-white disabled:opacity-50" disabled={busy || card.title.trim() === ""} onClick={onSend}>Envoyer</button>
              </div>
            </div>
            <TrashButton label="Retirer la tâche" onClick={onRemove} />
          </div>
          {choices.map((prop) => {
            const chosen = prop.values.find((value) => value.key === card.props[prop.key]);
            return (
              <div key={prop.key} className="mt-1 flex items-center gap-1">
                <span className="h-3 w-3 shrink-0 rounded-full border border-black/10" style={{ background: chosen?.color || "transparent" }} />
                <select className="crm-board-field min-w-0 flex-1 rounded-lg border border-slate-200 px-1 py-1 text-xs" value={card.props[prop.key] || ""} onChange={(event) => onChange({ props: { ...card.props, [prop.key]: event.target.value } })} onBlur={onBlur}>
                  <option value="">{prop.label}</option>
                  {prop.values.map((value) => <option key={value.key} value={value.key}>{value.label}</option>)}
                </select>
              </div>
            );
          })}
          <NoticeLine notice={card.notice} />
        </div>
        <div className="crm-card-pad">
          <div className="crm-card-pad-sheet crm-task-note flex h-full min-h-0 flex-col p-3">
            <FichePagePicker fiche={fiche} note={card.note} onUse={(content) => { onChange({ note: content }); setPadKey((value) => value + 1); onBlur(); }} />
            <div className="crm-notepad min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200">
              {mounted ? <NotePad key={padKey} ref={padRef} initial={card.note} kanban portaled upload={upload} onChange={() => undefined} /> : null}
            </div>
            <div className="mt-2 flex justify-end gap-1">
              <button type="button" className="rounded-full crm-fill px-2.5 py-1 text-[10px] font-semibold text-white disabled:opacity-50" disabled={busy} onClick={() => void saveNote()}>{busy ? "Enregistrement…" : "Enregistrer"}</button>
              <button type="button" className="crm-pill rounded-full px-2.5 py-1 text-[10px] font-semibold" disabled={busy} onClick={() => void closeNote()}>Fermer</button>
            </div>
          </div>
        </div>
      </div>
      {nextTitle ? (
        <button type="button" className="crm-board-next crm-fill" disabled={busy} onClick={onAdvance}>→ {nextTitle}</button>
      ) : null}
    </li>
  );
}

function SendCard({
  card,
  column,
  boardName,
  ficheName,
  choices,
  busy,
  error,
  onClose,
  onSend,
}: {
  card: { title: string; note: string; props: Record<string, string> };
  column: string;
  boardName: string;
  ficheName: string;
  choices: PropChoice[];
  busy: boolean;
  error: string;
  onClose: () => void;
  onSend: (day: string, mode: "copy" | "move") => Promise<void>;
}) {
  const [day, setDay] = useState(todayStamp);
  const lines = choices.flatMap((prop) => {
    const chosen = prop.values.find((value) => value.key === card.props[prop.key]);
    if (!chosen || !chosen.label.trim()) return [];
    return [{ key: prop.key, label: prop.label, value: chosen.label, color: chosen.color || "#64748b" }];
  });
  const where = kanbanOn() ? "le kanban principal" : "la semaine";
  const preview = visibleNote(card.note);
  return (
    <Modal onClose={onClose} closeDisabled={busy} sheetClass="crm-send crm-pop rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
      {(close) => (
        <>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Envoyer</p>
          <h2 className="mt-1 text-lg font-semibold">{card.title.trim() || "Tâche"}</h2>
          <p className="mt-2 text-sm text-slate-500">Une tâche est créée sur {where}. Copier laisse la carte ici. Transférer la retire.</p>
          <dl className="mt-4 space-y-2 text-sm">
            <div>
              <dt className="text-xs font-medium text-slate-500">Description</dt>
              <dd className="mt-0.5 whitespace-pre-wrap">{preview || "Sans description"}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500">Propriétés</dt>
              <dd className="mt-1 flex flex-wrap gap-1">
                {lines.length > 0 ? lines.map((line) => (
                  <span key={line.key} className="crm-send-prop inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: line.color }} />
                    {line.label} · {line.value}
                  </span>
                )) : <span className="text-slate-500">Aucune</span>}
              </dd>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              <div>
                <dt className="text-xs font-medium text-slate-500">Fiche</dt>
                <dd>{ficheName}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-slate-500">Kanban</dt>
                <dd>{boardName}{column ? ` · ${column}` : ""}</dd>
              </div>
            </div>
          </dl>
          <div className={busy ? "pointer-events-none mt-4 opacity-50" : "mt-4"}>
            <p className="mb-1 text-xs font-medium text-slate-500">Date</p>
            <DateField value={day} onChange={setDay} />
            {day ? <p className="mt-1 text-xs text-slate-500">{frDay(day)}</p> : null}
          </div>
          {error ? <p className="mt-3 text-sm text-rose-700">{error}</p> : null}
          <div className="mt-4 flex items-center justify-end gap-2">
            <button type="button" className="crm-pill rounded-full px-3 py-1.5 text-sm font-semibold" disabled={busy} onClick={close}>Annuler</button>
            <button type="button" className="rounded-full crm-fill px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50" disabled={busy || !day || card.title.trim() === ""} onClick={() => void onSend(day, "copy")}>{busy ? "Envoi…" : "Copier"}</button>
            <button type="button" className="rounded-full crm-fill px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50" disabled={busy || !day || card.title.trim() === ""} onClick={() => void onSend(day, "move")}>{busy ? "Envoi…" : "Transférer"}</button>
          </div>
        </>
      )}
    </Modal>
  );
}

function visibleNote(note: string): string {
  return note.replace(/\n*<!--BLOCKS_JSON[\s\S]*$/, "").trim();
}

function frDay(day: string): string {
  const match = day.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return day;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const label = date.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return label.charAt(0).toUpperCase() + label.slice(1);
}
